import { ThemeEvents } from '@theme/events';

/**
 * <bulk-buy-save>
 *
 * Product-page widget for a bulk buy & save offer. The tiers and their prices
 * are rendered server-side in Liquid; this element only handles selection:
 *
 *   - tapping a tier sets the quantity selector to that tier's quantity
 *   - changing the quantity by hand re-highlights the matching tier
 *
 * Note the tier shown as active here reflects this product's quantity alone.
 * The discount itself pools quantity across every product in the offer, so a
 * shopper with items already in their cart can qualify for a better tier than
 * the one highlighted.
 */
class BulkBuySave extends HTMLElement {
  connectedCallback() {
    // Delegated so the listener survives the section morph that runs on every
    // variant change (which replaces the tier buttons wholesale).
    this.addEventListener('click', this.#onClick);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    this.#highlight();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-bulk-tier]') : null;
    if (!(target instanceof HTMLElement)) return;

    event.preventDefault();
    this.#setQuantity(parseInt(target.dataset.quantity ?? '1', 10) || 1);
  };

  #onQuantityUpdate = () => this.#highlight();

  // The morph that follows a variant change re-renders the tiers from Liquid,
  // wiping the highlight this element applied.
  #onVariantUpdate = () => this.#highlight();

  /**
   * The product form's quantity selector. It lives outside this block, so it's
   * looked up through the nearest shared ancestor rather than held as a ref.
   * @returns {any | null}
   */
  get #quantitySelector() {
    const scope = this.closest('product-form-component') ?? this.closest('.shopify-section') ?? document;
    return scope.querySelector('quantity-selector-component');
  }

  /** @returns {number} The quantity currently in the selector. */
  #currentQuantity() {
    const values = this.#quantitySelector?.getCurrentValues?.();
    return typeof values?.value === 'number' ? values.value : 1;
  }

  /**
   * Sets the quantity selector to a tier's quantity. The variant's own min/max
   * rules still apply — those are the theme's stock limits, not ours.
   * @param {number} quantity
   */
  #setQuantity(quantity) {
    const selector = this.#quantitySelector;
    if (!selector) return;

    const values = selector.getCurrentValues?.();
    const effectiveMax = selector.getEffectiveMax?.();

    let target = Math.max(values?.min ?? 1, quantity);
    if (typeof effectiveMax === 'number') target = Math.min(effectiveMax, target);

    selector.setValue?.(String(target));
    selector.onQuantityChange?.();
    selector.updateButtonStates?.();

    this.#highlight();
  }

  /** Marks the best tier the current quantity reaches. */
  #highlight() {
    const quantity = this.#currentQuantity();
    const tiers = [...this.querySelectorAll('[data-bulk-tier]')];

    let best = null;
    let bestQuantity = 0;

    for (const tier of tiers) {
      const tierQuantity = parseInt(tier.getAttribute('data-quantity') ?? '0', 10) || 0;
      if (tierQuantity <= quantity && tierQuantity >= bestQuantity) {
        best = tier;
        bestQuantity = tierQuantity;
      }
    }

    for (const tier of tiers) {
      tier.setAttribute('aria-checked', tier === best ? 'true' : 'false');
    }
  }
}

if (!customElements.get('bulk-buy-save')) {
  customElements.define('bulk-buy-save', BulkBuySave);
}
